import { FC } from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import { Alert } from "reactstrap";

import ConfirmationModal from "@components/shared/ConfirmationModal/ConfirmationModal";
import Loader from "@components/shared/Loader/Loader";
import { AMENITY_CONSTANTS } from "@constants/amenities";
import { useAddAmenities } from "@hooks/useAddAmenities/useAddAmenities";
import { AddAmenitiesProps } from "@models/components/amenity.model";

import { AmenityCard } from "../AmenityCard/AmenityCard";
import AmenityInfoModal from "../AmenityInfoModal/AmenityInfoModal";
import { BookedAmenitiesTable } from "../BookedAmenitiesTable/BookedAmenitiesTable";
import NextAvailabilityPopover from "../NextAvailabilityPopover/NextAvailabilityPopover";

const AddAmenities: FC<AddAmenitiesProps> = props => {
  const {
    activeSelectionsCount,
    amenityAddedUnavailableError,
    amenityUnavailableError,
    availableAmenities,
    bookedDataSelections,
    calculateMaxQty,
    changedAmenityNames,
    confirmRemove,
    confirmTarget,
    CustomToast,
    getAmenityPriceInfo,
    getAvailableCardProps,
    getBookedCardProps,
    handleCloseConfirm,
    handleCloseInfo,
    handleCloseNextAvailability,
    hasConflict,
    isAmenitiesLoading,
    isBookingParamsChanged,
    isConfirmOpen,
    isInfoOpen,
    isNextAvailabilityLoading,
    isNextAvailabilityPopoverOpen,
    nextAvailabilityData,
    nextAvailabilityTarget,
    open,
    resourcePrices,
    selectedInfoAmenity,
    t,
    toggle,
    unavailableAddedAmenityIds,
    unavailableAddedAmenityNames,
    unavailableBookedAmenityNames,
    unavailableMandatoryAmenityIds,
    unavailableMandatoryAmenityNames,
  } = useAddAmenities(props);
  const renderListAlert = (translationKey: string, items: string[]) => {
    if (items.length === 0) return null;

    const fullText = t(translationKey, { amenities: "SPLIT_PLACEHOLDER" });
    const parts = fullText.split("SPLIT_PLACEHOLDER");
    const header = parts[0]?.trim();
    const footer = parts[1]?.trim()?.replace(/^[.,\s]+/, "");

    return (
      <div className="fg-custom-warning-alert mb-3">
        <div className="fg-alert-icon-wrapper">
          <FaExclamationCircle />
        </div>
        <div className="fg-alert-content">
          {header && <div className="fg-alert-header">{header}</div>}
          <ul className="fg-alert-list">
            {items.map(name => (
              <li key={name}>{name}</li>
            ))}
          </ul>
          {footer && <div className="fg-alert-footer">{footer}</div>}
        </div>
      </div>
    );
  };

  const isAmenityAddedUnavailableWarning =
    !hasConflict &&
    changedAmenityNames.length === 0 &&
    (unavailableAddedAmenityNames.length > 0 ||
      (isBookingParamsChanged && unavailableBookedAmenityNames.length > 0));

  if (
    !availableAmenities.length &&
    !bookedDataSelections.length &&
    !unavailableMandatoryAmenityNames.length &&
    !isAmenitiesLoading
  )
    return null;

  return (
    <div className="fg-add-amenities-wrapper mb-3">
      {isAmenitiesLoading ? (
        <Loader isLoading={true} />
      ) : (
        <div className="accordion fg-amenity-accordion">
          <div className="accordion-item fg-accordion-item">
            <h2 className="accordion-header fg-accordion-header">
              <button
                className={`accordion-button ${open === "1" ? "" : "collapsed"}`}
                type="button"
                onClick={() => toggle("1")}
                aria-expanded={open === "1"}
              >
                <div className="d-flex justify-content-between align-items-center w-100 me-2">
                  <div className="d-flex align-items-center">
                    <span className="fg-accordion-title">
                      {t("optionalExtras")}
                    </span>
                    {activeSelectionsCount >
                      AMENITY_CONSTANTS.DEFAULT_SELECTED_AMENITY && (
                      <span className="fg-selected-badge">
                        {activeSelectionsCount} {t("selected")}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </h2>
            <div
              className={`fg-accordion-collapse-wrapper ${open === "1" ? "open" : ""}`}
            >
              <div className="fg-accordion-collapse-content">
                <div className="accordion-body fg-accordion-body">
                  {hasConflict ? (
                    <Alert color="danger" className="small p-2 mb-3">
                      {t("bookingEdit.amenityChangeConflictWarning")}
                    </Alert>
                  ) : (
                    <>
                      {changedAmenityNames.length > 0
                        ? renderListAlert(
                            isBookingParamsChanged
                              ? "bookingEdit.amenityPriceChangedParamWarning"
                              : "bookingEdit.amenityPriceChangedWarning",
                            changedAmenityNames
                          )
                        : isBookingParamsChanged &&
                            (unavailableBookedAmenityNames.length > 0 ||
                              unavailableAddedAmenityNames.length > 0)
                          ? renderListAlert(
                              "bookingEdit.amenityAddedUnavailableWarning",
                              [
                                ...unavailableBookedAmenityNames,
                                ...unavailableAddedAmenityNames,
                              ]
                            )
                          : unavailableBookedAmenityNames.length > 0
                            ? renderListAlert(
                                "bookingEdit.amenityUnavailableWarning",
                                unavailableBookedAmenityNames
                              )
                            : unavailableAddedAmenityNames.length > 0
                              ? renderListAlert(
                                  "bookingEdit.amenityAddedUnavailableWarning",
                                  unavailableAddedAmenityNames
                                )
                              : !amenityUnavailableError &&
                                !amenityAddedUnavailableError &&
                                renderListAlert(
                                  "bookingEdit.mandatoryAmenityUnavailableWarning",
                                  unavailableMandatoryAmenityNames
                                )}
                    </>
                  )}

                  <div className="fg-amenity-sections d-flex flex-column gap-4">
                    {/* Section 1: BOOKED AT TIME OF BOOKING */}
                    {bookedDataSelections.length >
                      AMENITY_CONSTANTS.DEFAULT_SELECTED_AMENITY && (
                      <div className="fg-amenity-section">
                        <h6 className="fg-section-header text-uppercase mb-3">
                          {t("bookedAtTimeOfBooking")}
                        </h6>
                        <BookedAmenitiesTable
                          bookedDataSelections={bookedDataSelections}
                          getBookedCardProps={getBookedCardProps}
                          t={t}
                        />
                      </div>
                    )}

                    {/* Section 2: AVAILABLE TO ADD */}
                    {availableAmenities.length > 0 && (
                      <div className="fg-amenity-section">
                        <h6 className="fg-section-header text-uppercase">
                          {t("availableToAdd")}
                        </h6>
                        <div className="fg-amenity-cards-container">
                          {availableAmenities.map(amenity => {
                            const cardProps = getAvailableCardProps(amenity);
                            return (
                              <AmenityCard
                                key={cardProps.id}
                                amenityAddedUnavailableErrorIds={
                                  amenityAddedUnavailableError ||
                                  isAmenityAddedUnavailableWarning
                                    ? [
                                        ...unavailableAddedAmenityIds,
                                        ...unavailableMandatoryAmenityIds,
                                      ]
                                    : []
                                }
                                {...cardProps}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <AmenityInfoModal
        isOpen={isInfoOpen}
        toggle={handleCloseInfo}
        amenity={selectedInfoAmenity}
        maxQuantity={
          selectedInfoAmenity ? calculateMaxQty(selectedInfoAmenity) : undefined
        }
        priceInfo={
          selectedInfoAmenity
            ? getAmenityPriceInfo(selectedInfoAmenity)
            : AMENITY_CONSTANTS.DEFAULT_PRICE_INFO
        }
        currency={resourcePrices?.currency || ""}
      />

      <ConfirmationModal
        isOpen={isConfirmOpen}
        updateModalState={handleCloseConfirm}
        submitAction={confirmRemove}
        modalHeaderText={t("bookingEdit.removeAmenity")}
        modalBodyText={t("bookingEdit.removePaidAmenityConfirm", {
          name: confirmTarget?.title || "",
        })}
        submitButtonText={t("remove")}
        cancelButtonText={t("cancel")}
      />

      <NextAvailabilityPopover
        target={nextAvailabilityTarget}
        isOpen={isNextAvailabilityPopoverOpen}
        isLoading={isNextAvailabilityLoading}
        amenityTitle={nextAvailabilityData?.amenityTitle || ""}
        nextAvailableDatetime={
          nextAvailabilityData?.nextAvailableDatetime || null
        }
        noAvailability={!!nextAvailabilityData?.noAvailability}
        onClose={handleCloseNextAvailability}
      />

      <CustomToast />
      <Tooltip
        id="amenity-mandatory-tooltip"
        place="top"
        style={{ zIndex: 1000 }}
        className="fg-tooltip-container"
      />
    </div>
  );
};

export default AddAmenities;
